import {Observable, of} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {catchError, map} from "rxjs/operators";
import {DebugService} from "../debug/debug.service";
import {RequestAnfrage} from "../model/request.anfrage";
import {inject, Injectable} from "@angular/core";
import {AdvisorEventService} from "./advisor-event.service";

/**
 * Generic service wor speaking with the backend. Can be extended by advisor specific services.
 */
@Injectable({
  providedIn: 'root'
})
export class BackendService {

  /**
   * Name of the request id in the local storage. Should be overwritten by child classes
   */
  protected requestIdName: string;

  /**
   * Path of the backend endpoint to save the data of the current request obejct.
   * Must be overwritten by child classes.
   * @protected
   */
  protected backendDataEndpoint: string;

  /**
   * The unique requestID for the current request
   * @private
   */
  protected requestId: string = null;

  /**
   * remember the last data that we saved to avoid too much performance need on saving requests
   */
  protected lastData: string;

  /**
   * HOlds the currently used advisor
   */
  currentAdvisor: string = null;

  /**
   * informs an embedding page about the saved request
   */
  protected advisorEventService = inject(AdvisorEventService);

  /**
   * constructor.
   * @param http
   * @param debugService
   */
  protected constructor(protected http: HttpClient, protected debugService: DebugService) {}

  /**
   * Sets the current advisor
   * @param advisor
   */
  public setAdvisor(advisor: string) {
    this.currentAdvisor = advisor;
  }

  /**
   * Returns the current advisor
   */
  public getAdvisor(): string {
    return this.currentAdvisor;
  }

  /**
   * Sends a request to the backend and returns the response data.
   * @param url
   * @param method
   * @param data
   * @protected
   */
  protected doRequest(url: string, method: string, data: any = {}): Observable<any> {
    return this.http[method](url, data).pipe(
      catchError((error) => {
        // an error accured whil getting the data from the backend
        console.error("BackendService: Error while getting data from the backend.", error);
        return of(error.hasOwnProperty("error") ? error.error : error);
      })
    );
  }

  /**
   * add some debugdata to the debugservice.
   * @param debugData
   */
  addDebug(debugData: any): void {
    Object.keys(debugData).forEach(key => {
      let name = key.replace(/_([a-z])/g, (g) => {
        return " " + g[1].toUpperCase();
      });
      name = name[0].toUpperCase() + name.slice(1);
      this.debugService.addItem(key, name, debugData[key]);
    });
  }

  /**
   * resets the local requestId
   */
  resetRequestId(): void {
    try {
      if (typeof localStorage === "object") {
        // save to localstorage to alwyas have the same request ID
        localStorage.removeItem(this.requestIdName);
      }
    }
    catch (e) {

    }
    this.requestId = null;
  }

  /**
   * Returns the requestId from the localStorage if there is one saved.
   */
  getLocalRequestid(): string {
    try {
      if (typeof localStorage === 'object') {
        // check if we have already a requestid in the localStorage
        return localStorage.getItem(this.requestIdName);
      }
    }
    catch (e) {
    }
    return null;
  }

  /**
   * Save the requestId to the local storage
   */
  saveRequestId(): void {
    try {
      if (typeof localStorage === 'object') {
        // save to localStorage to always have the same request ID
        localStorage.setItem(this.requestIdName, this.requestId);
      }
    }
    catch (e) {
    }
  }

  /**
   * Get's a (new) requestId from the backend and save it to the local storage.
   */
  public getRequestIdFromBackend(): Observable<string> {
    return this.doRequest('/id/' + this.currentAdvisor, 'get', {}).pipe(
      map((res) => {
        // we've got an id, return it but check if there was a parallel request and the id already set
        this.requestId = this.requestId ?? res.id;
        this.saveRequestId();
        return this.requestId;
      })
    );
  }

  /**
   * Returns the unique ID for the current request.
   * Generates the ID from the backend if there is none available at the moment
   */
  public getRequestId(): Observable<string> {
    try {
      if (this.requestId === null && typeof localStorage === 'object') {
        // check if we have already a requestId in the localStorage
        const localRequestId = this.getLocalRequestid();
        if (localRequestId !== null) {
          this.requestId = localRequestId;
        }
      }
    }
    catch (e) {
    }

    if (this.requestId === null || typeof this.requestId === "undefined" || this.requestId === "undefined") {
      // get request ID from backend
      return this.getRequestIdFromBackend();
    }
    this.debugService.addItem('requestId', 'RequestId', this.requestId);
    return of(this.requestId);
  }

  /**
   * Get energy map hash
   */
  public getEnergyMapHash($requestId): Observable<string> {
    return this.doRequest('/energy_map_hash/' + $requestId, 'get', {}).pipe(
      map((res) => {
        return res?.energy_map_hash;
      })
    );
  }

  /**
   * Returns all images for infobuttons of an advisor.
   * @param advisor
   */
  getInfobuttonImages(advisor: string): Observable<any> {
    return this.doRequest('/image/infobutton/' + advisor, 'get');
  }

  /**
   * Sends the data of one step to the backend.
   *
   * @param requestId
   * @param step
   * @param data
   */
  submitStep(requestId: string, step: string, data: any): Observable<any> {
    return this.doRequest(step + '/' + requestId, 'post', data);
  }

  /**
   * Sends feedback to the backend
   * @param data
   */
  sendFeedback(data: any): Observable<boolean> {
    const advisor = this.getAdvisor();

    return this.doRequest('/feedback/' + advisor, 'post', data);
  }

  /**
   * creates a new redirect URL in the backend.
   * @param data
   */
  getRedirectString(data): Observable<string> {
    return this.doRequest('/redirect/create', 'put', data);
  }

  /**
   * sends the users input to the backend, so it can be savend there.
   * @param anfrage
   */
  saveData(anfrage: RequestAnfrage): void {
    // only save the data on the server if it has changed, ignore the last update date for this
    const lastUpdate = anfrage.lastupdate;
    anfrage.lastupdate = null;
    if(this.lastData === JSON.stringify(anfrage)) {
      // do not save the data, has not changed
      return;
    }
    this.lastData = JSON.stringify(anfrage);

    setTimeout(() => {
      this.getRequestId().subscribe(requestId => {
        // remeber the last path that the users sees
        anfrage.lastPath = location.pathname;
        // remeber the current status
        this.lastData = JSON.stringify(anfrage);
        // set the lastupdatedate back
        anfrage.lastupdate = lastUpdate;

        // send data to backend
        this.http.post<{success?: boolean, hash?: string | null}>(this.backendDataEndpoint + requestId, anfrage).subscribe(result => {
          // the request exists in the backend now, the hash is only set if the portal may get it
          this.advisorEventService.request(requestId, result?.hash ?? null);
        }, error => {
          console.error("an error occured while saving the data");
        });
      });
    }, 0);
  }
}
